// src/pages/Home.js

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';

import loKtrLogo from '../assets/loKtrLogo.png';
import TopBar from '../components/TopBar/TopBar';
import Background from '../components/Background/Background';
import ContactUsDialog from '../components/ContactUsDialog/ContactUsDialog';

import packageJson from '../../package.json';

const Home = () => {
  // === LAYOUT POSITIONING CONTROLS ===
  const gridTopPosition = '70px';
  const bodyFontSize = 'clamp(13px, min(1.5vw, 2.5vh), 28px)';

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <Background>
      {/* === ANIMATIONS === */}
      <style>
        {`
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}
      </style>

      {/* === INJECT TOP MENU BAR === */}
      <TopBar activeButton='home' />

      {/* === SINGLE COLUMN CENTERED LAYOUT === */}
      <Box
        sx={{
          position: 'absolute',
          top: gridTopPosition,
          bottom: '20px',
          left: '20px',
          right: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            width: '100%',
            maxWidth: '600px',
            height: '100%',
            minHeight: 0,
          }}>
          {/* Row 1: Logo Image and Slogan */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              minHeight: 0,
              overflow: 'hidden',
            }}>
            <img
              src={loKtrLogo}
              alt='loKtr Logo'
              style={{ maxWidth: '100%', maxHeight: '75%', objectFit: 'contain' }}
            />
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 600,
                color: '#000000',
                mt: 1.5,
                textAlign: 'center',
                animation: 'fadeIn 3s ease-in-out forwards', // Simple 3s fade in to black
              }}>
              BRING YOUR ACHIEVEMENTS TO LIFE
            </Typography>
          </Box>

          {/* Row 2: Welcome Message (Centered) */}
          <Box
            sx={{
              flex: 1.5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              p: { xs: 1.5, md: 3 },
              color: '#000000',
              minHeight: 0,
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 2,
              }}>
              Welcome to{' '}
              <Box component='span' sx={{ color: 'royalblue', fontWeight: 700 }}>
                {' '}
                loKtr
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 2,
              }}>
              A visual tool for managing and showcasing your company's project
              achievements and supply capabilities
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
              }}>
              Click through and try our demo web app
            </Typography>
          </Box>

          <Box sx={{ flex: 0.75, minHeight: 0 }} />
        </Box>
      </Box>

      {/* ========================================================================= */}
      {/* === UNIVERSAL FOOTER === */}
      {/* ========================================================================= */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backgroundColor: 'transparent',
          zIndex: 10,
        }}>
        <Typography
          onClick={handleOpenDialog} // Triggers the contact dialog
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
            fontWeight: 400,
            color: '#000000',
            opacity: 0.9,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { textDecoration: 'underline', opacity: 1 },
          }}>
          info@loktr.net
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            Version: {packageJson.version}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            (c) RTA Technology Pty Ltd. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* === EXTRACTED CONTACT FORM POPUP === */}
      <ContactUsDialog open={dialogOpen} onClose={handleCloseDialog} />
    </Background>
  );
};

export default Home;
